import type {
    ComponentStageData,
    ComponentStageHeuristicDefinition,
    ConversionDal,
    InnerElementStageData
} from '../../types'
import {getStylePointer} from '../../conversionDal/utils/pointer'
import type {StyleEntryItem} from '../../conversionDal/types'
import type {Pointer} from '@wix/document-services-types'
import _ from 'lodash'
import {FONT_SIZE_CLASSES_RULES} from '@wix/document-manager-utils'
// eslint-disable-next-line wix-editor/no-internal-import
import {CSS_PROPERTIES} from '@wix/component-protocol/schema'
import {SemanticTypes} from '../../preprocess/conversionDataBuilders'
import {isCSSVariable} from '../../utils/cssUtils'

const transformSizeValue = (value: string): string | undefined => {
    const sizeRegex = /^(\d+(\.\d+)?)(px|spx)$/
    const match = value.match(sizeRegex)

    if (!match) {
        return undefined
    }

    const [, num] = match
    const originalSize = parseFloat(num)

    const rule = FONT_SIZE_CLASSES_RULES.find(
        ({minSourceFontSize, maxSourceFontSize}) =>
            originalSize > minSourceFontSize && originalSize <= (maxSourceFontSize ?? Infinity)
    )

    if (!rule) {
        return undefined
    }

    if (rule.scaleProportionally) {
        return undefined
    }

    const fontSize = rule.targetFontSize ?? originalSize

    return Number.isInteger(fontSize) ? `${fontSize}spx` : `${fontSize.toFixed(3).replace(/\.000$/, '')}spx`
}

const transformFont = (fontStr: string): string | undefined => {
    if (isCSSVariable(fontStr)) {
        return undefined
    }

    const fontSizeRegex = /(?<![-\w\/])(\d+(?:\.\d+)?)(px|spx)\b/

    if (!fontSizeRegex.test(fontStr)) {
        return undefined
    }

    let shouldReturnUndefined = false

    const result = fontStr.replace(fontSizeRegex, match => {
        const transformed = transformSizeValue(match)

        if (!transformed) {
            shouldReturnUndefined = true
        }

        return transformed ?? match
    })

    return shouldReturnUndefined ? undefined : result
}

const transformFontSize = (fontSizeStr: string): string | undefined => {
    if (isCSSVariable(fontSizeStr)) {
        return undefined
    }

    const fontSizeRegex = /(?<![-\w])(\d+(\.\d+)?)(px|spx)\b/

    if (!fontSizeRegex.test(fontSizeStr)) {
        return undefined
    }

    let shouldReturnUndefined = false

    const result = fontSizeStr.replace(fontSizeRegex, match => {
        const transformed = transformSizeValue(match)

        if (!transformed) {
            shouldReturnUndefined = true
        }

        return transformed ?? match
    })

    return shouldReturnUndefined ? undefined : result
}

const extractAndTransformFontSizeFromFont = (fontStr: string): string | undefined => {
    if (isCSSVariable(fontStr)) {
        return undefined
    }

    const fontSizeRegex = /(?<![-\w\/])(\d+(?:\.\d+)?)(px|spx)\b/
    const match = fontStr.match(fontSizeRegex)

    if (!match) {
        return undefined
    }

    const [rawMatch] = match

    return transformSizeValue(rawMatch)
}

interface FontPropertyConfig {
    getValue(baseStyleSettings: StyleEntryItem): string | undefined
    transformFn(value: string): string | undefined
}

const FONT_PROPERTY_CONFIGS: FontPropertyConfig[] = [
    {
        getValue: settings => settings?.cssProperties?.fontSize,
        transformFn: transformFontSize
    },
    {
        getValue: settings => settings?.cssProperties?.font,
        transformFn: extractAndTransformFontSizeFromFont
    },
    {
        getValue: settings => settings?.metaData?.default?.cssProperties?.fontSize,
        transformFn: transformFontSize
    },
    {
        getValue: settings => settings?.metaData?.default?.cssProperties?.font,
        transformFn: extractAndTransformFontSizeFromFont
    }
]

const processCustomProperties = (
    conversionDal: ConversionDal,
    compStylePointer: Pointer,
    baseStyleSettings: StyleEntryItem
) => {
    const customPropertiesInfo = baseStyleSettings?.metaData?.cssCustomPropertiesInfo || {}

    Object.entries(customPropertiesInfo).forEach(([customPropKey, propertyType]) => {
        const customValue =
            _.get(baseStyleSettings, ['cssCustomProperties', customPropKey]) ||
            _.get(baseStyleSettings, ['metaData', 'default', 'cssCustomProperties', customPropKey])

        if (!customValue) {
            return
        }

        let transformedValue: string | undefined

        if (propertyType === CSS_PROPERTIES.CSS_PROPERTY_TYPE.font) {
            transformedValue = transformFont(customValue)
        } else if (propertyType === CSS_PROPERTIES.CSS_PROPERTY_TYPE.fontSize) {
            transformedValue = transformFontSize(customValue)
        }

        if (transformedValue) {
            conversionDal.modify(compStylePointer, (value: any) =>
                _.merge({}, value, {
                    cssCustomProperties: {
                        [customPropKey]: transformedValue
                    }
                })
            )
        }
    })
}

const applyFontTransformation = (
    conversionDal: ConversionDal,
    compStylePointer: Pointer,
    config: FontPropertyConfig,
    fontValue: string
) => {
    const transformedFontSize = config.transformFn(fontValue)

    if (!transformedFontSize) {
        return
    }

    conversionDal.modify(compStylePointer, (value: any) =>
        _.merge({}, value, {
            cssProperties: {fontSize: transformedFontSize}
        })
    )
}

const scaleFontSizesCore = (conversionDal: ConversionDal, stylePointer: Pointer, baseStyleSettings: StyleEntryItem) => {
    processCustomProperties(conversionDal, stylePointer, baseStyleSettings)

    for (const fontConfig of FONT_PROPERTY_CONFIGS) {
        const fontValue = fontConfig.getValue(baseStyleSettings)

        if (fontValue) {
            return applyFontTransformation(conversionDal, stylePointer, fontConfig, fontValue)
        }
    }
}

const scaleFontSizes = (componentStageData: ComponentStageData) => {
    const {conversionDal, componentPointer, initialSnapshot} = componentStageData
    const compStylePointer = getStylePointer(componentPointer)
    const baseStyleSettings = initialSnapshot.getValue(compStylePointer) as StyleEntryItem

    return scaleFontSizesCore(conversionDal, compStylePointer, baseStyleSettings)
}

export const scaleInnerElementFontSizes = (innerElementStageData: InnerElementStageData) => {
    const {conversionDal, innerElementPointer, initialSnapshot} = innerElementStageData
    const innerElementStylePointer = getStylePointer(innerElementPointer)
    const baseStyleSettings = initialSnapshot.getValue(innerElementStylePointer) as StyleEntryItem

    return scaleFontSizesCore(conversionDal, innerElementStylePointer, baseStyleSettings)
}

export const fontHeuristics: ComponentStageHeuristicDefinition[] = [
    {
        predicate: ({conversionDal, componentPointer}: ComponentStageData) =>
            conversionDal.get(componentPointer).semanticType === SemanticTypes.BUTTON,
        apply: _.noop
    },
    {
        predicate: ({componentPointer, initialSnapshot}) =>
            !!initialSnapshot.getValue(getStylePointer(componentPointer)),
        apply: (componentStageData: ComponentStageData) => scaleFontSizes(componentStageData),
        heuristicCode: 'SCALE FONT SIZES'
    }
]
